"use client";

import { useState } from "react";
import {
	ArrowDown,
	Pencil,
	Trash,
	ChevronRight,
	ChevronLeft,
} from "lucide-react";
import sticker14 from "../../../../public/sticker14.png";
import sticker13 from "../../../../public/sticker13.png";
import { useRouter } from "next/navigation";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { AnimatePresence, motion } from "framer-motion";
import { CircolariCardsProps } from "@/types/circolari";
import { toast } from "sonner";
import SectionTitle from "@/components/shared/SectionTitle";
import { authFetch } from "@/lib/authClient";

// Normalizza URL (supporta assoluti, /relative e "uploads/...")
function absUrl(src?: string) {
	if (!src) return "";
	const s = String(src).trim();
	if (/^https?:\/\//i.test(s)) return s;
	const base = (process.env.NEXT_PUBLIC_BACKEND_URL || "").replace(/\/+$/, "");
	if (s.startsWith("/")) return base ? `${base}${s}` : s;
	return base
		? `${base}/${s.replace(/^\/+/, "")}`
		: `/${s.replace(/^\/+/, "")}`;
}

export default function CircolariCards({
	circolari,
	isAdmin,
	onDelete,
}: CircolariCardsProps) {
	const [selectedToDelete, setSelectedToDelete] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const router = useRouter();

	const handleEdit = (id: string) => {
		router.push(`/admin/circolare/edit-circolare/?id=${id}`);
	};

	const confirmDelete = async () => {
		if (!selectedToDelete) return;

		try {
			const res = await authFetch(`/api/circolari/${selectedToDelete}`, {
				method: "DELETE",
			});

			if (!res.ok) {
				let msg = "";
				try {
					const j = await res.clone().json();
					msg = j?.error || JSON.stringify(j);
				} catch {
					msg = await res.text();
				}
				toast.error(`Eliminazione fallita (${res.status}) ${msg}`);
				return;
			}

			onDelete?.(selectedToDelete);
			setSelectedToDelete(null);
			toast.success("Circolare eliminata.");
		} catch {
			toast.error("Errore durante l'eliminazione.");
		}
	};

	const pageSize = 3;
	const pageCount = Math.max(1, Math.ceil(circolari.length / pageSize));
	const pageItems = circolari.slice(
		currentPage * pageSize,
		currentPage * pageSize + pageSize
	);

	return (
		<motion.section
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.4 }}
		>
			<section>
				<SectionTitle
					title1="Le "
					title2="Circolari"
					leftIcon={sticker14}
					rightIcon={sticker13}
					iconSizeLeft={150}
					iconSizeRight={150}
					hideIconOnMobile="right"
				/>

				<AnimatePresence mode="wait">
					<div className="flex flex-col gap-6 mt-8">
						{pageItems.map((item) => {
							const pdfHref = absUrl(
								(item as any).pdf || (item as any).fileUrl
							);

							return (
								<motion.div
									key={item._id}
									initial={{ opacity: 0, scale: 0.95 }}
									animate={{ opacity: 1, scale: 1 }}
									exit={{ opacity: 0, scale: 0.95 }}
									transition={{ duration: 0.4 }}
									className="bg-(--card) flex flex-col lg:flex-row items-start lg:items-center justify-between border-y-[3px] border-(--card-foreground) p-6 w-full gap-4"
								>
									<div className="flex-1">
										<h3 className="text-lg md:text-xl font-semibold text-(--card-foreground) mb-2">
											{item.title}
										</h3>
										<p className="text-(--foreground) text-sm md:text-base mb-1 leading-relaxed line-clamp-4">
											{item.description}
										</p>
										<p className="text-(--muted-foreground) text-sm italic">
											{new Date(item.date).toLocaleDateString("it-IT", {
												day: "2-digit",
												month: "long",
												year: "numeric",
											})}
										</p>
									</div>

									<div className="mt-2 lg:mt-0 flex flex-col sm:flex-row lg:flex-row flex-wrap items-start gap-3 shrink-0">
										<a
											href={pdfHref || "#"}
											target="_blank"
											rel="noopener noreferrer"
											download
											onClick={(e) => {
												if (!pdfHref) {
													e.preventDefault();
													toast.error("PDF non disponibile.");
												}
											}}
											className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition
                        ${
													pdfHref
														? "bg-[#10b0ff] text-white hover:bg-[#0a9cd8]"
														: "bg-gray-300 text-gray-600 cursor-not-allowed"
												}`}
											aria-disabled={!pdfHref}
											title={
												pdfHref ? "Scarica/Apri PDF" : "PDF non disponibile"
											}
										>
											<ArrowDown className="w-4 h-4" />
											Scarica PDF
										</a>

										{isAdmin && (
											<>
												<Button
													onClick={() => handleEdit(item._id)}
													className="w-[100px] cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-(--btn-bg) hover:bg-(--btn-bg) text-(--btn-foreground) rounded-full"
												>
													<Pencil className="w-4 h-4" />
													Modifica
												</Button>

												<Dialog>
													<DialogTrigger asChild>
														<Button
															onClick={() => setSelectedToDelete(item._id)}
															className="w-[100px] cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-red-600 hover:bg-red-600 text-white rounded-full"
														>
															<Trash className="w-4 h-4" />
															Elimina
														</Button>
													</DialogTrigger>
													<DialogContent className="bg-(--card)">
														<DialogHeader>
															<DialogTitle>Sei sicuro?</DialogTitle>
														</DialogHeader>
														<p>
															Questa operazione è irreversibile. Vuoi davvero
															eliminare la circolare?
														</p>
														<DialogFooter className="mt-4">
															<Button
																variant="outline"
																onClick={() => setSelectedToDelete(null)}
															>
																Annulla
															</Button>
															<Button
																variant="destructive"
																onClick={confirmDelete}
															>
																Elimina
															</Button>
														</DialogFooter>
													</DialogContent>
												</Dialog>
											</>
										)}
									</div>
								</motion.div>
							);
						})}
					</div>
				</AnimatePresence>

				{/* Paginazione */}
				<div className="flex justify-center items-center gap-2 mt-6">
					<button
						onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
						disabled={currentPage === 0}
						aria-label="Pagina precedente"
						title="Pagina precedente"
					>
						<ChevronLeft className="cursor-pointer w-5 h-5 text-(--card-foreground)" />
					</button>

					{Array.from({ length: pageCount }, (_, index) => (
						<button
							key={index}
							onClick={() => setCurrentPage(index)}
							className={`cursor-pointer px-3 py-1 rounded ${
								index === currentPage
									? "bg-[#10b0ff] text-white"
									: "bg-(--card) text-(--foreground)"
							}`}
							aria-current={index === currentPage ? "page" : undefined}
							aria-label={`Vai alla pagina ${index + 1}`}
						>
							{index + 1}
						</button>
					))}

					<button
						onClick={() =>
							setCurrentPage((prev) => Math.min(prev + 1, pageCount - 1))
						}
						disabled={currentPage >= pageCount - 1}
						aria-label="Pagina successiva"
						title="Pagina successiva"
					>
						<ChevronRight className="cursor-pointer w-5 h-5 text-[#10b0ff]" />
					</button>
				</div>
			</section>
		</motion.section>
	);
}
